import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { getCountryName } from '../../../utils/countries';
import { formatMsmeStatus } from '../../../utils/formatters';
import { CheckCircle, Clock, AlertTriangle } from 'lucide-react';

const ApplicationCard = ({ application, onViewDetails }) => {
  const getStatusBadge = (status) => {
    // Normalize status to lowercase to handle both uppercase and lowercase values from backend
    const normalizedStatus = (status || '').toLowerCase().replace(/_/g, '_');

    const statusConfig = {
      pending_level_1: { variant: 'warning', label: 'Pending first approval' },
      pending_level_2: { variant: 'info', label: 'Pending Level 2' },
      approved: { variant: 'success', label: 'Approved' },
      rejected: { variant: 'destructive', label: 'Rejected' },
      pending: { variant: 'warning', label: 'Pending' }
    };

    const config = statusConfig[normalizedStatus] || statusConfig.pending;
    return (
      <Badge variant={config.variant} className="text-xs">
        {config.label}
      </Badge>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDaysAgo = (date) => {
    const today = new Date();
    const submissionDate = new Date(date);
    const diffTime = Math.abs(today - submissionDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <Card className="border border-slate-200 shadow-sm bg-white hover:shadow-md transition-all duration-300">
      <CardContent className="p-6">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-slate-900 truncate">{application.company_name}</h3>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-600 flex-wrap">
              <span className="flex items-center gap-1.5">
                <Icon name="Calendar" size={14} />
                <span>Submitted {formatDate(application.created_at)}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Icon name="Clock" size={14} />
                <span>{getDaysAgo(application.created_at)} days ago</span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            {getStatusBadge(application.status)}
            {application.acknowledgement?.status === 'PENDING' && (
              <span className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                <Clock className="w-3 h-3 mr-1" />
                AWAITING ACKNOWLEDGEMENT
              </span>
            )}
            {(application.acknowledgement?.status === 'APPROVED' || application.acknowledgement?.status === 'RESOLVED') && (
              <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                <CheckCircle className="w-3 h-3 mr-1" />
                VENDOR ACKNOWLEDGED
              </span>
            )}
            {application.acknowledgement?.status === 'CONFLICTED' && (
              <span className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
                <AlertTriangle className="w-3 h-3 mr-1" />
                VENDOR CONFLICTED
              </span>
            )}
          </div>
        </div>

        <div className="h-px bg-slate-200 my-4" />

        {/* Info Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
              <Icon name="Building2" size={18} className="text-slate-700" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500 mb-0.5">MSME Status</p>
              <p className="text-sm font-semibold text-slate-900 truncate">{formatMsmeStatus(application.msme_status)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
              <Icon name="MapPin" size={18} className="text-slate-700" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500 mb-0.5">Country</p>
              <p className="text-sm font-semibold text-slate-900 truncate">{getCountryName(application.country_origin)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
              <Icon name="FileText" size={18} className="text-slate-700" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500 mb-0.5">Documents</p>
              <p className="text-sm font-semibold text-slate-900">{typeof application.documentsCount === 'number' ? application.documentsCount : (application.documents ? application.documents.length : 0)} Uploaded</p>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="flex items-center gap-4 mb-4 text-sm text-slate-600 flex-wrap">
          <span className="flex items-center gap-1.5">
            <Icon name="User" size={14} />
            <span className="truncate">{application.contact_person_name}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <Icon name="Mail" size={14} />
            <span className="truncate">{application.email}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <Icon name="Phone" size={14} />
            <span>{application.phone_number}</span>
          </span>
        </div>

        <div className="h-px bg-slate-200 my-4" />

        {/* View Details Button */}
        <div className="flex justify-end">
          <Button
            variant="default"
            onClick={() => onViewDetails(application)}
            iconName="ArrowRight"
            iconPosition="right"
          >
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ApplicationCard;